import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiAlertCircle, FiArrowRight } from "react-icons/fi";
import SteffiDevice from "../components/SteffiDevice";
import { useWaitlist } from "../components/Waitlist";
import { useSteffiCopy } from "../content";

/* A handful of motes drifting through the warm light behind Steffi.
   Deterministic, so the layout never shifts between renders — but properly
   scattered: a plain linear seed lands every mote on one diagonal. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const hash = (n) => {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
};

function useParticles(count = 16) {
  return useMemo(
    () =>
      Array.from({ length: count }, (_, i) => {
        const seed = hash(i + 1);
        const seed2 = hash(i * 2.3 + 7.9);
        return {
          left: `${8 + seed * 84}%`,
          top: `${28 + seed2 * 62}%`,
          size: 3 + seed2 * 6,
          dur: `${16 + seed * 16}s`,
          delay: `${-seed2 * 24}s`,
          dx: `${(seed - 0.5) * 90}px`,
          peak: 0.24 + seed2 * 0.34,
        };
      }),
    [count]
  );
}

export default function Hero() {
  const { HERO } = useSteffiCopy();
  const openWaitlist = useWaitlist();
  const particles = useParticles();
  const sectionRef = useRef(null);
  const copyRef = useRef(null);
  const unitRef = useRef(null);
  const glowRef = useRef(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  /* The address typed here travels into the waitlist dialog, which still asks
     for consent (and the city), so nothing is stored from the hero itself. */
  const onJoin = (event) => {
    event.preventDefault();
    const value = email.trim();
    if (value && !EMAIL_RE.test(value)) {
      setEmailError(HERO.emailInvalid);
      return;
    }
    openWaitlist(value);
  };

  /* Steffi opens her eyes a moment after the page settles. */
  const [awake, setAwake] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAwake(true), 700);
    return () => clearTimeout(t);
  }, []);

  /* The copy recedes and Steffi eases forward as the visitor starts to scroll. */
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const desktop = window.matchMedia("(min-width: 1024px)");

    const update = () => {
      const h = section.offsetHeight || window.innerHeight;
      const p = Math.min(1, Math.max(0, window.scrollY / h));
      const eased = p * p;

      if (copyRef.current) {
        if (desktop.matches) {
          copyRef.current.style.opacity = String(Math.max(0, 1 - p * 2.1));
          copyRef.current.style.transform = `translateY(${(-p * 70).toFixed(1)}px)`;
          copyRef.current.style.filter = p > 0.06 ? `blur(${(p * 7).toFixed(2)}px)` : "none";
        } else {
          copyRef.current.style.removeProperty("opacity");
          copyRef.current.style.removeProperty("transform");
          copyRef.current.style.removeProperty("filter");
        }
      }
      if (unitRef.current) {
        unitRef.current.style.transform =
          `translateY(${(eased * -34).toFixed(1)}px) scale(${(1 + eased * 0.11).toFixed(4)})`;
      }
      if (glowRef.current) {
        glowRef.current.style.transform = `scale(${(1 + eased * 0.22).toFixed(3)})`;
      }
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative flex min-h-[100svh] items-center overflow-hidden pb-16 pt-[83px] lg:pb-10 lg:pt-[61px]"
      style={{
        background:
          "var(--bg)",
      }}
    >
      {/* warm light behind the product */}
      <div
        ref={glowRef}
        aria-hidden
        className="pointer-events-none absolute right-[-6%] top-[6%] h-[68vh] w-[68vh] transition-transform duration-1000 lg:right-[6%] lg:top-[10%]"
        style={{ transitionTimingFunction: "cubic-bezier(.2,.8,.2,1)" }}
      >
        <div
          className="glow-blob absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 50% 45%, rgba(215,180,138,.5) 0%, rgba(234,220,207,.42) 38%, rgba(252,247,243,0) 70%)",
          }}
        />
      </div>
      <div
        aria-hidden
        className="glow-blob pointer-events-none absolute left-[-14%] bottom-[-18%] h-[46vh] w-[46vh]"
        style={{
          background: "radial-gradient(circle, rgba(184,199,177,.28) 0%, rgba(252,247,243,0) 68%)",
          animationDelay: "-4s",
        }}
      />

      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        {particles.map((p, i) => (
          <span
            key={i}
            className="particle"
            style={{
              left: p.left,
              top: p.top,
              width: p.size,
              height: p.size,
              animationDuration: p.dur,
              animationDelay: p.delay,
              "--dx": p.dx,
              "--peak": p.peak,
            }}
          />
        ))}
      </div>

      <div className="relative mx-auto grid w-full max-w-[1440px] grid-cols-1 items-center gap-y-4 px-6 lg:grid-cols-[1.02fr_0.98fr] lg:gap-x-10 lg:px-10">
        <div ref={copyRef} className="order-2 max-w-[38rem] lg:order-1">
          <p className="t-eyebrow reveal is-in">{HERO.eyebrow}</p>

          <h1 className="t-hero mt-5 !text-[clamp(3rem,min(7vw,11vh),5.5rem)] !leading-[0.98] lg:mt-6">
            {HERO.title.map((line, i) => (
              <span
                key={line}
                className="-mb-[0.25em] block overflow-hidden pb-[0.25em]"
                style={{
                  animation: `steffi-rise 1000ms cubic-bezier(.2,.8,.2,1) ${140 + i * 110}ms both`,
                }}
              >
                {line}
              </span>
            ))}
          </h1>

          <p
            className="t-body mt-6 max-w-[31rem] text-[color:var(--ink-2)] lg:mt-8"
            style={{ animation: "steffi-rise 900ms cubic-bezier(.2,.8,.2,1) 680ms both" }}
          >
            {HERO.body}
          </p>

          <form
            onSubmit={onJoin}
            noValidate
            className="mt-8 w-full lg:mt-10"
            style={{ maxWidth: "36rem", animation: "steffi-rise 900ms cubic-bezier(.2,.8,.2,1) 800ms both" }}
          >
            {/* One pill: the field and, inside it, the call to action that opens
                the waitlist dialog. Sizes are inline because steffi-sizing.css
                scales the Tailwind size utilities down inside .steffi; the
                field and button sizes live in steffi.css (.hero-email-*). */}
            <div
              className={`flex items-center gap-2 rounded-full border bg-[color:var(--bg)] transition ${
                emailError
                  ? "border-[#a4593f]"
                  : "border-[color:var(--border)] focus-within:border-[color:var(--accent)]"
              }`}
              style={{ padding: "0.4rem 0.4rem 0.4rem 0" }}
            >
              <input
                id="hero-email"
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (emailError) setEmailError("");
                }}
                placeholder={HERO.emailPlaceholder}
                aria-label={HERO.emailPlaceholder}
                autoComplete="email"
                aria-invalid={Boolean(emailError)}
                aria-describedby={emailError ? "hero-email-error" : undefined}
                className="hero-email-input min-w-0 flex-1 bg-transparent text-[color:var(--ink)] outline-none placeholder:text-[color:var(--ink-3)]"
              />
              <button
                type="submit"
                className="hero-email-button flex shrink-0 items-center rounded-full bg-[color:var(--color-primary)] font-semibold text-white transition hover:bg-[color:var(--accent)]"
              >
                {HERO.emailLabel}
                <FiArrowRight aria-hidden className="hero-email-arrow" />
              </button>
            </div>
            {emailError && (
              <p
                id="hero-email-error"
                role="alert"
                className="mt-2 flex items-start gap-2 px-4 text-[0.9375rem] leading-snug text-[#a4593f]"
              >
                <FiAlertCircle aria-hidden className="mt-0.5 shrink-0" /> {emailError}
              </p>
            )}
          </form>
        </div>

        {/* No transform, filter or opacity may sit above the device: the render
            blends with the page so the glow reads through its white backdrop. */}
        <div className="order-1 lg:order-2">
          {/* sized from the viewport height — the render is 882 × 1253, so the
              product always resolves inside one screen. Live face, hearts on
              hover. */}
          <div className="relative mx-auto w-[clamp(12rem,50vw,20rem)] lg:w-[clamp(20rem,60vh,34rem)]">
            <SteffiDevice
              variant="front"
              priority
              awake={awake}
              unitRef={unitRef}
              alt={HERO.alt}
            />
          </div>
        </div>
      </div>

      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-7 hidden justify-center lg:flex"
        style={{ animation: "steffi-rise 900ms cubic-bezier(.2,.8,.2,1) 1200ms both" }}
      >
        <span className="flex flex-col items-center gap-2 text-[0.75rem] uppercase tracking-[0.18em] text-[color:var(--ink-3)]">
          {HERO.scroll}
          <span className="block h-9 w-px bg-gradient-to-b from-[color:var(--border)] to-transparent" />
        </span>
      </div>
    </section>
  );
}
